import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
  Paper,
  Tooltip,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { toast } from "react-toastify";
import Loader from "../../../../components/loader/loader";
import { useParams, useNavigate } from "react-router-dom";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";
import { ContentCopy, CopyAllOutlined } from "@mui/icons-material";

type Param = {
  _id: string;
  parameterName: string;
  interpretation: string;
  patientTypeIds?: string[]; // array of patientType ids for that parameter
};

type PatientType = {
  _id: string;
  patientTypeName: string;
};

type Row = {
  id: string;
  patientTypeId: string;
  min: number;
  max: number;
  criticalMin: number;
  criticalMax: number;
  minmaxinwords: string;
  aboveMaxBg: string;
  aboveMaxText: string;
  normalRangeBg: string;
  normalRangeText: string;
  belowMinBg: string;
  belowMinText: string;
  unit_cutOff: string;
  parameterType?: string;
  resultTemplateId?: string;
};

type Block = {
  id: string;
  serverId?: string; // optional id returned by server for existing entries
  parameterId: string;
  parametersName?: string;
  interpretation: string;
  parameterIdentifier: string;
  computationFormula: string;
  formulaError?: string; // validation error for computation formula
  orderBy: number | "";
  rows: Row[];
};

const generateId = () => Math.random().toString(36).slice(2, 9);

const TestParameters: React.FC<any> = () => {
  const [parameters, setParameters] = useState<Param[]>([]);
  const [patientTypes, setPatientTypes] = useState<PatientType[]>([]);
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [serverBlocks, setServerBlocks] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [rowLength, setRowLength] = useState(0);
  const { id } = useParams();
  const [testName, setTestName] = useState("");
  const [charges, setCharges] = useState("");
  const [testType, setTestType] = useState(false);
  const [testCode, setTestCode] = useState(0);
  const [units, setUnits] = useState<{ _id: string; unitName: string }[]>([]);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [resultTemplates, setResultTemplates] = useState<any[]>([]);
  const [parameterTypes, setParameterTypes] = useState<any[]>([]);
  const navigate = useNavigate();

  let testId = id;

  const base = process.env.REACT_APP_LAB_URL;

  /* ---------- Data fetching (your original logic) ---------- */
  const fetchParameters = async () => {
    try {
      if (!base) return;
      const res = await axios.get(`${base}/parameters/getAllBySearch`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      console.log(res?.data.data, "data");
      setParameters(res?.data?.data || []);
    } catch (err: any) {
      // console.error("Error fetching parameters", err);
      toast.error(err.response.message || "Failed to fetch parameters");
    }
  };

  const fetchPatientTypes = async () => {
    try {
      if (!base) return;
      const res = await axios.get(`${base}/patient-type/getAllBySearch`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      if (res?.data?.isSuccess) {
        console.log(res?.data?.data, "pat");
        setRowLength(res?.data?.data.length);
        setPatientTypes(res?.data?.data || []);
      }
    } catch (err: any) {
      console.error("Error fetching patient types", err);
      toast.error(err?.message || "Error fetching patient types");
    }
  };

  const fetchExistingForTest = async (tid: string) => {
    try {
      if (!base) return;
      const res = await axios.get(
        `${base}/test-paramerters/getAllTestParameterByTestId/${tid}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (res?.data?.isSuccess && Array.isArray(res.data?.data)) {
        setServerBlocks(res?.data?.data);
        setLoading(false);
      }
    } catch (err: any) {
      console.warn(
        "Could not fetch existing test parameters for testId:",
        tid,
        err
      );
      toast.error(err?.message || "Could not fetch existing test parameters");
    }
  };
  const fetchTestById = async () => {
    try {
      setLoading(true);
      const resAll = await axios.get(`${base}/tests/getOneLabTest/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      const data = resAll.data?.data ?? resAll.data;
      if (data.testResultType.includes("Lipid profile test")) {
        setTestType(true);
      }
      setTestName(data.testName || "");
      setCharges(data.testCharges || "");
      setTestCode(data.testCode || "");
    } catch (error: any) {
      console.error("Error fetching test by id", error);
        toast.error(error?.response.data.message || "Error fetching test details");
      return null;
    } finally {
      setLoading(false);
    }
  };
  const fetchUnits = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/testUnits/getAllByPagination`,
        {
          params: { pageNo: 1, count: 1000 }, // adjust if API requires pagination
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = res.data?.data ?? [];
      setUnits(data);
    } catch (err) {
      console.error("fetchUnits error", err);
    }
  };
  const fetchResultTemplates = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/result-template/getAllBySearch`,
        {
          // adjust if API requires pagination
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = res.data?.data ?? [];
      setResultTemplates(data);
    } catch (err: any) {
      console.error("fetchUnits error", err);
      toast.error(err?.message || "Error fetching result templates");

    }
  };

  const fetchParameterTypes = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/parameter-type/getAllBySearch`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const data = res.data?.data ?? [];
      setParameterTypes(data);
    } catch (err: any) {
      console.error("fetchParameterTypes error", err);
      toast.error(err?.response?.data?.message || "Error fetching parameter types");
    }
  };

  useEffect(() => {
    if (!testId) {
      setLoading(false);
    }
    const loadAll = async () => {
      if (base) {
        await Promise.all([
          fetchParameters(),
          fetchPatientTypes(),
          fetchUnits(),
          fetchResultTemplates(),
          fetchParameterTypes(),
        ]);
        if (testId) {
          await fetchExistingForTest(testId);
          await fetchTestById();
        }
      }
    };
    loadAll();
  }, [testId]);

  useEffect(() => {
    if (!parameters.length || !serverBlocks.length) return;
    const loaded: Block[] = serverBlocks.map((b: any) => ({
      id: generateId(),
      serverId: b._id,
      parameterId:
        parameters.find(p => p.parameterName === b.parameterName)?._id || "",
      parametersName: b.parameterName,
      orderBy: b.orderBy ?? "",
      interpretation: b.interpretation || "",
      parameterIdentifier: b.parameterIdentifier || "",
      computationFormula: b.computationFormula || "",
      formulaError: "",
      rows:
        Array.isArray(b.patientTypes) && b.patientTypes.length
          ? b.patientTypes.map((pr: any) => ({
              id: generateId(),
              patientTypeId: pr.patientTypeId || "",
              min: pr.min ?? "",
              max: pr.max ?? "",
              criticalMin: pr.criticalMin ?? "",
              criticalMax: pr.criticalMax ?? "",
              unit_cutOff: pr.unit_cutOff ?? "",
              parameterType: pr.parameterType ?? "",
              resultTemplateId: pr.resultTemplateId || "",
              minmaxinwords: pr.minMaxInWord,
              belowMinText: pr.belowMinValueText ?? "Low",
              belowMinBg: pr.belowMinValueColor,
              normalRangeBg: pr.noramalRangeColor,
              normalRangeText: pr.noramalRangeText ?? "Normal",
              aboveMaxText: pr.aboveMaxValueText ?? "High",
              aboveMaxBg: pr.aboveMaxValueColor,
            }))
          : [],
    }));

    // console.log(loaded, "loaded");

    setBlocks(loaded);
  }, [parameters, serverBlocks]);

  const handleAddBlock = () => {
    // console.log(parameters.length, blocks.length);
    if (blocks.length > parameters.length - 1) {
      toast.info("Do not Exceed the Paramter length");
      return;
    }
    setBlocks(prevBlocks => {
      const maxOrder = prevBlocks.reduce((max, block) => {
        const orderNum = Number(block.orderBy) || 0;
        return orderNum > max ? orderNum : max;
      }, 0);

      const newBlock: Block = {
        id: generateId(),
        parameterId: "",
        orderBy: maxOrder + 1,
        interpretation: "",
        parameterIdentifier: "",
        computationFormula: "",
        formulaError: "",
        rows: [],
      };

      return [newBlock, ...prevBlocks];
    });
  };

  const handleCloseBlock = async (blockId: string) => {
    const block = blocks.find(b => b.id === blockId);
    setBlocks(prev => prev.filter(b => b.id !== blockId));
    if (!block?.serverId) {
      // toast.success("Parameter block removed");
      return;
    }

    try {
      const res = await axios.delete(
        `${base}/test-paramerters/removeBtId/${block.serverId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      if (res?.data?.isSuccess)
        toast.success(
          res?.data?.message || "Parameter block removed successfully"
        );
      else
        toast.error(res?.data?.message || "Failed to remove parameter block");
    } catch (err: any) {
      console.error("Error deleting block:", err);
      toast.error(err?.message || "Error deleting parameter block");
    }
  };

  const handleParameterChange = (blockId: string, paramId: string) => {
    if (!paramId) {
      // user cleared the input → reset this block, but keep the rows the user filled in
      setBlocks(prev =>
        prev.map(b =>
          b.id === blockId
            ? {
                ...b,
                parameterId: "",
                parametersName: "",
                interpretation: "",
                parameterIdentifier: "",
                computationFormula: "",
                formulaError: "",
              }
            : b
        )
      );
      return;
    }
    const param = parameters.find(p => p._id === paramId);

    const isDuplicate = blocks.some(
      b => b.parameterId === paramId && b.id !== blockId
    );
    if (isDuplicate) {
      toast.warn("This parameter is already added!");
    }

    // const limitedPatientTypes = patientTypes.slice(0, 3);
    const initialRows: Row[] = patientTypes.map((pt: any) => ({
      id: generateId(),
      patientTypeId: pt._id,
      min: 0,
      max: 0,
      criticalMin: 0,
      criticalMax: 0,
      minmaxinwords: "",
      aboveMaxBg: "",
      aboveMaxText: "",
      normalRangeBg: "",
      normalRangeText: "",
      belowMinBg: "",
      belowMinText: "",
      unit_cutOff: "",
      parameterType: "",
      resultTemplateId: "",
    }));

    setBlocks(prev =>
      prev.map(b =>
        b.id === blockId
          ? {
              ...b,
              parameterId: paramId,
              parametersName: param?.parameterName || "",
              interpretation: param?.interpretation || b.interpretation || "",
              formulaError: "",
              // keep whatever the user already entered for the patient-type rows;
              // only seed the default rows when the block is still empty
              rows: b.rows.length ? b.rows : initialRows,
            }
          : b
      )
    );
  };

  const handleAddRow = (blockId: string) => {
    // console.log(blocks.length, "len");

    const newRow: Row = {
      id: generateId(),
      patientTypeId: "",
      min: 0,
      max: 0,
      criticalMin: 0,
      criticalMax: 0,
      minmaxinwords: "",
      aboveMaxBg: "",
      aboveMaxText: "",
      normalRangeBg: "",
      normalRangeText: "",
      belowMinBg: "",
      belowMinText: "",

      parameterType: "",
      unit_cutOff: "",
      resultTemplateId: "",
    };

    setBlocks(prev =>
      prev.map(b => {
        if (b.id === blockId) {
          if (b.rows.length >= rowLength) {
            toast.info("Row cannot exceed more");
            return b;
          }
          const updatedRows = [...b.rows, newRow];
          return { ...b, rows: updatedRows };
        }
        return b;
      })
    );
  };

  const handleRemoveRow = (blockId: string, rowId: string) => {
    setBlocks(prev =>
      prev.map(b =>
        b.id === blockId
          ? { ...b, rows: b.rows.filter(r => r.id !== rowId) }
          : b
      )
    );
  };

  const numericFields = new Set(["min", "max", "criticalMin", "criticalMax"]);
  const handleRowChange = (
    blockId: string,
    rowId: string,
    field: keyof Row,
    value: string | number
  ) => {
    setBlocks(prev =>
      prev.map(b =>
        b.id === blockId
          ? {
              ...b,
              rows: b.rows.map(r =>
                r.id === rowId
                  ? {
                      ...r,
                      [field]: numericFields.has(String(field))
                        ? value === ""
                          ? ""
                          : Number(value)
                        : value,
                    }
                  : r
              ),
            }
          : b
      )
    );
  };

  const handleOrderByChange = (blockId: string, value: string) => {
    const numeric = value === "" ? "" : Math.max(0, Number(value));
    setBlocks(prev =>
      prev.map(b => (b.id === blockId ? { ...b, orderBy: numeric } : b))
    );
  };

  // Validation function for computation formula
  const validateComputationFormula = (formula: string, allBlocks: Block[]) => {
    if (!formula.trim()) return "";

    // Extract identifiers from formula (letters, numbers, underscores)
    const identifierPattern = /[a-zA-Z_][a-zA-Z0-9_]*/g;
    const identifiersInFormula = formula.match(identifierPattern) || [];

    // Get all declared parameter identifiers
    const declaredIdentifiers = allBlocks
      .filter(block => block.parameterIdentifier.trim() !== "")
      .map(block => block.parameterIdentifier.trim());

    // Find undeclared identifiers
    const undeclaredIdentifiers = identifiersInFormula.filter(
      identifier =>
        !declaredIdentifiers.includes(identifier) &&
        // Exclude common mathematical functions and operators
        ![
          "sin",
          "cos",
          "tan",
          "log",
          "sqrt",
          "abs",
          "min",
          "max",
          "pow",
          "exp",
          "floor",
          "ceil",
          "round",
        ].includes(identifier.toLowerCase())
    );

    if (undeclaredIdentifiers.length > 0) {
      const uniqueUndeclared = Array.from(new Set(undeclaredIdentifiers));
      return `Undeclared identifier(s): ${uniqueUndeclared.join(", ")}`;
    }

    return "";
  };

  const handleComputationFormulaChange = (blockId: string, value: string) => {
    setBlocks(prev => {
      const updatedBlocks = prev.map(b =>
        b.id === blockId ? { ...b, computationFormula: value } : b
      );

      // Validate the formula after updating
      const currentBlock = updatedBlocks.find(b => b.id === blockId);
      if (currentBlock) {
        const error = validateComputationFormula(value, updatedBlocks);
        return updatedBlocks.map(b =>
          b.id === blockId ? { ...b, formulaError: error } : b
        );
      }

      return updatedBlocks;
    });
  };

  const handleParameterIdentifierChange = (blockId: string, value: string) => {
    setBlocks(prev => {
      const updatedBlocks = prev.map(b =>
        b.id === blockId ? { ...b, parameterIdentifier: value } : b
      );

      // Re-validate all computation formulas since available identifiers changed
      return updatedBlocks.map(block => ({
        ...block,
        formulaError: block.computationFormula
          ? validateComputationFormula(block.computationFormula, updatedBlocks)
          : "",
      }));
    });
  };

  const handleSaveBlock = async (blockId: string) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    if (!block.parametersName) {
      toast.info("Please select a Parameter");
      return;
    }

    // Check for formula validation errors
    if (block.formulaError) {
      toast.error("Please fix computation formula errors before saving");
      return;
    }

    const isDuplicate = blocks.find(
      b => b.id !== block.id && b.parameterId === block.parameterId
    );

    if (isDuplicate) {
      toast.info("Parameter will not be same");
      return;
    }

    const allHavePatientType = block.rows.every(r => r.patientTypeId);
    const allHaveResultTemplate = block.rows.every(r => r.resultTemplateId);

    if (!allHavePatientType) {
      toast.info("Patient type not be Empty");
      return;
    }
    // if (!allHaveResultTemplate) {
    //   toast.info("Result Template not be Empty");
    //   return;
    // }
    const ids = block.rows.map(r => r.patientTypeId);
    const hasDuplicates = new Set(ids).size !== ids.length;
    if (hasDuplicates) {
      toast.info("Patient type not be the same ");
      return;
    }

    const parameter = parameters.find(p => p._id === block.parameterId);
    const payload = {
      testId,
      parameterName: parameter?.parameterName || block.parametersName || "",
      orderBy: block.orderBy === "" ? 0 : Number(block.orderBy),
      interpretation: block.interpretation || "",
      parameterIdentifier: block.parameterIdentifier || "",
      computationFormula: block.computationFormula || "",
      patientTypes: block.rows.map(r => ({
        patientTypeId: r.patientTypeId,
        min: Number(r.min),
        max: Number(r.max),
        criticalMin: Number(r.criticalMin),
        parameterType: r.parameterType || "",
        criticalMax: Number(r.criticalMax),
        unit_cutOff: r.unit_cutOff,
        resultTemplateId: r.resultTemplateId || "",
        minMaxInWord: r.minmaxinwords,
        belowMinValueText: r.belowMinText || "Low",
        belowMinValueColor: r.belowMinBg || testType ? "#091fb0" : "",
        noramalRangeText: r.normalRangeText || "Normal",
        noramalRangeColor: r.normalRangeBg || testType ? "#31F218" : "",
        aboveMaxValueText: r.aboveMaxText || "High",
        aboveMaxValueColor: r.aboveMaxBg || testType ? "#a81103" : "",
      })),
    };

    // console.log(payload, "payload");

    try {
      if (block.serverId) {
        const res = await axios.patch(
          `${base}/test-paramerters/updateById/${block.serverId}`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (res?.data?.isSuccess)
          toast.success(res.data.message || "Updated parameter block");
        else {
          const errorMessage = res?.data?.message;
          if (errorMessage === "Some of the patient type ids are not exist") {
            toast.error("Patient Type should not be the same");
          } else {
            toast.error(errorMessage || "Failed to add parameter block");
          }
        }
      } else {
        const res = await axios.post(
          `${base}/test-paramerters/addTestParameter`,
          payload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        if (res?.data?.isSuccess) {
          toast.success(res.data.message || "Added parameter block");
          const createdId = res?.data?.data?._id || res?.data?.data?.id;
          if (createdId)
            setBlocks(prev =>
              prev.map(b =>
                b.id === block.id ? { ...b, serverId: createdId } : b
              )
            );
        } else {
          const errorMessage = res?.data?.message;

          if (errorMessage === "Some of the patient type ids are not exist") {
            toast.error("Patient Type should not be the same");
          } else {
            toast.error(errorMessage || "Failed to add parameter block");
          }
        }
      }
    } catch (err: any) {
      console.error("Save error:", err);
      toast.error(err?.message || "Error saving parameter block");
    }
  };
  const handleOpenDeleteModal = (blockId: string) => {
    const block = blocks.find(b => b.id === blockId);
    if (block?.parametersName === "") {
      setBlocks(prev => prev.filter(b => b.id !== blockId));
      return;
    }
    setSelectedBlockId(blockId);
    setIsDeleteModalOpen(true);
  };
  const handleDeleteConfirm = () => {
    if (selectedBlockId) {
      handleCloseBlock(selectedBlockId);
    }
    setIsDeleteModalOpen(false);
    setSelectedBlockId(null);
  };

  const handleCopyToAll = (blockId: string, sourceRow: Row) => {
    // console.log(sourceRow, "roe");
    setBlocks(prev =>
      prev.map(b =>
        b.id === blockId
          ? {
              ...b,
              rows: b.rows.map(r =>
                r.id === sourceRow.id
                  ? r
                  : {
                      ...r,
                      min: sourceRow.min,
                      max: sourceRow.max,
                      criticalMin: sourceRow.criticalMin,
                      criticalMax: sourceRow.criticalMax,
                      minmaxinwords: sourceRow.minmaxinwords,
                      aboveMaxText: sourceRow.aboveMaxText,
                      aboveMaxBg: sourceRow.aboveMaxBg,
                      belowMinText: sourceRow.belowMinText,
                      belowMinBg: sourceRow.belowMinBg,
                      normalRangeText: sourceRow.normalRangeText,
                      normalRangeBg: sourceRow.normalRangeBg,
                      unit_cutOff: sourceRow.unit_cutOff,
                      parameterType: sourceRow.parameterType || "",
                      resultTemplateId: sourceRow.resultTemplateId || "",
                    }
              ),
            }
          : b
      )
    );
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Box display="flex" gap={3} flexWrap="wrap">
            <Typography variant="body1">
              <span style={{ fontWeight: 500 }}>Test Name: </span>
              <span style={{ color: "#616161" }}>{testName}</span>
            </Typography>
            <Typography variant="body1">
              <span style={{ fontWeight: 500 }}>Charges: </span>
              <span style={{ color: "#616161" }}>{charges}</span>
            </Typography>
            <Typography variant="body1">
              <span style={{ fontWeight: 500 }}>Test Code: </span>
              <span style={{ color: "#616161" }}>{testCode}</span>
            </Typography>
          </Box>
          <Box>
            {/* <Tooltip title={""}>
              <span> */}
            <Button
              variant="outlined"
              size="small"
              sx={{
                mr: 1,
              }}
              onClick={() => navigate("/settings/test-tab")}
            >
              Back To TestList
            </Button>
            {/* </span>
            </Tooltip> */}
            <Tooltip title={testId ? "Add Parameter" : "Create a Test First"}>
              <span>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddBlock}
                  disabled={!testId}
                >
                  Add Parameter
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        {blocks.length === 0 && (
          <Paper
            sx={{
              p: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 1,
            }}
            elevation={0}
          >
            {loading ? (
              <Loader />
            ) : (
              <Typography variant="body2" color="text.secondary">
                No parameters added yet. Click "Add Parameter" to get started.
              </Typography>
            )}
          </Paper>
        )}

        <Box mt={1}>
          {blocks.map(block => (
            <Paper
              key={block.id}
              sx={{
                p: 2.5,
                mb: 2,
                borderLeft: "4px solid",
                borderRight: "4px solid",

                borderLeftColor: theme => theme.palette.primary.main,
                borderRightColor: theme => theme.palette.primary.main,
                backgroundColor: "#f3f6fb",
              }}
              elevation={1}
            >
              <Grid container spacing={1} alignItems="center">
                <Grid container spacing={1} alignItems="center" sx={{ xs: 12 }}>
                  <Grid item xs={12} lg={2.2}>
                    {block.parameterId || !block.parametersName ? (
                      <Autocomplete
                        size="small"
                        options={parameters}
                        className="custom-input"
                        getOptionLabel={(opt: any) => opt.parameterName || ""}
                        isOptionEqualToValue={(option, value) =>
                          option._id === value._id
                        }
                        value={
                          parameters.find(p => p._id === block.parameterId) ||
                          null
                        }
                        onChange={(_, newValue) =>
                          handleParameterChange(block.id, newValue?._id || "")
                        }
                        renderOption={(props, option) => (
                          <li {...props} key={option._id}>
                            {option.parameterName}
                          </li>
                        )}
                        renderInput={params => (
                          <TextField
                            {...params}
                            label="Parameter"
                            size="small"
                            sx={{
                              "& .MuiInputBase-root": {
                                height: 30,
                                fontSize: "0.8rem",
                              },
                              "& .MuiInputLabel-root": {
                                fontSize: "0.80rem",
                                top: "-2px",
                              },
                            }}
                          />
                        )}
                      />
                    ) : (
                      <TextField
                        size="small"
                        value={block.parametersName || ""}
                        label="Parameter"
                        onChange={e =>
                          setBlocks(prev =>
                            prev.map(b =>
                              b.id === block.id
                                ? {
                                    ...b,
                                    parameterId: "",
                                    parametersName: e.target.value, // ✅ set directly
                                  }
                                : b
                            )
                          )
                        }
                        className="custom-input"
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 30,
                            fontSize: "0.8rem",
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "0.80rem",
                            top: "-2px",
                          },
                        }}
                      />
                    )}
                  </Grid>

                  <Grid item xs={12} lg={1.3}>
                    <TextField
                      size="small"
                      label="Order By"
                      type="number"
                      className="custom-input"
                      value={block.orderBy}
                      onChange={e =>
                        handleOrderByChange(block.id, e.target.value)
                      }
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 30,
                          fontSize: "0.8rem",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.80rem",
                          top: "-2px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} lg={2.2}>
                    <TextField
                      size="small"
                      label="Interpretation"
                      className="custom-input"
                      value={block.interpretation}
                      onChange={e =>
                        setBlocks(prev =>
                          prev.map(b =>
                            b.id === block.id
                              ? {
                                  ...b,
                                  interpretation: e.target.value,
                                }
                              : b
                          )
                        )
                      }
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 30,
                          fontSize: "0.8rem",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.80rem",
                          top: "-2px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} lg={2.2}>
                    <TextField
                      size="small"
                      label="Parameter Identifier"
                      className="custom-input"
                      value={block.parameterIdentifier}
                      onChange={e =>
                        handleParameterIdentifierChange(
                          block.id,
                          e.target.value
                        )
                      }
                      fullWidth
                      sx={{
                        "& .MuiInputBase-root": {
                          height: 30,
                          fontSize: "0.8rem",
                        },
                        "& .MuiInputLabel-root": {
                          fontSize: "0.80rem",
                          top: "-2px",
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} lg={2.2}>
                    <Tooltip
                      title={
                        block.formulaError ||
                        "Enter computation formula using parameter identifiers"
                      }
                      placement="top"
                      arrow
                    >
                      <TextField
                        size="small"
                        label="computation Formula"
                        className="custom-input"
                        value={block.computationFormula}
                        error={!!block.formulaError}
                        onChange={e =>
                          handleComputationFormulaChange(
                            block.id,
                            e.target.value
                          )
                        }
                        fullWidth
                        sx={{
                          "& .MuiInputBase-root": {
                            height: 30,
                            fontSize: "0.8rem",
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "0.80rem",
                            top: "-2px",
                          },
                        }}
                      />
                    </Tooltip>
                  </Grid>

                  <Grid item xs={12} lg={1.9} sx={{ display: "flex", gap: 1 }}>
                    <Tooltip
                      title={
                        block.serverId ? "Update Paramter" : "Save Paramter"
                      }
                    >
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSaveBlock(block.id)}
                          sx={{
                            fontSize: "0.75rem",
                            py: 0.3,
                            px: 0.8,
                            minWidth: "60px",
                          }}
                        >
                          {block.serverId ? "Update" : "Save"}
                        </Button>
                      </span>
                    </Tooltip>

                    <Tooltip title="Delete Paramter">
                      <span>
                        <Button
                          size="small"
                          variant="contained"
                          color="error"
                          onClick={() => handleOpenDeleteModal(block.id)}
                          sx={{
                            fontSize: "0.75rem",
                            py: 0.3,
                            px: 0.8,
                            minWidth: "60px",
                          }}
                        >
                          Delete
                        </Button>
                      </span>
                    </Tooltip>
                  </Grid>
                </Grid>

                <Grid item xs={12}>
                  <TableContainer
                    sx={{
                      overflowX: "auto",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                        height: "6px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#36a1eeb9",
                        borderRadius: "3px",
                        "&:hover": {
                          backgroundColor: "#94a3b8",
                        },
                      },
                      "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f8fafc",
                        borderRadius: "3px",
                      },
                      "&::-webkit-scrollbar-corner": {
                        backgroundColor: "#f8fafc",
                      },
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        // Prevent wrapping and keep compact paddings
                        "& .MuiTableCell-root": {
                          padding: "2px 4px",
                          whiteSpace: "nowrap",
                          verticalAlign: "middle",
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "0.8rem",
                          padding: "6px 8px",
                        },
                        "& .MuiMenuItem-root": {
                          fontSize: "0.8rem",
                          minHeight: "28px",
                        },
                        // Make table wide so headers don't wrap — adjust as needed

                        minWidth: testType ? 1600 : 800,
                      }}
                    >
                      <TableHead sx={{ backgroundColor: "#8fc9f3a1" }}>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              minWidth: "140px",
                            }}
                          >
                            Patient Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              // width: "80px",
                            }}
                          >
                            Critical Min
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              minWidth: "80px",
                              // width: "80px",
                            }}
                          >
                            Min
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              minWidth: "80px",
                              // width: "80px",
                            }}
                          >
                            Max
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                            Critical Max
                          </TableCell>

                          {/* NEW columns from design */}
                          {testType && (
                            <>
                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "12px" }}
                              >
                                Below min value text
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "12px" }}
                              >
                                Below min value background color
                              </TableCell>

                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "12px" }}
                              >
                                Normal range text
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "12px" }}
                              >
                                Normal range background color
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "12px" }}
                              >
                                Above max value text
                              </TableCell>
                              <TableCell
                                sx={{ fontWeight: 600, fontSize: "12px" }}
                              >
                                Above max value background color
                              </TableCell>
                            </>
                          )}

                          <TableCell sx={{ fontWeight: 600, fontSize: "12px" }}>
                            Min max value in words
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              minWidth: "120px",
                            }}
                          >
                            Unit / CutOff
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              minWidth: "120px",
                            }}
                          >Parameter Type
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              minWidth: "120px",
                            }}
                          >
                            
                            Template Type
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              width: "20px",
                            }}
                          >
                            <Tooltip title="Add Row">
                              <span>
                                <Button
                                  size="small"
                                  variant="contained"
                                  sx={{
                                    py: 0,
                                    px: 0,
                                    p: 0,
                                    minWidth: "25px",
                                  }}
                                  onClick={() => handleAddRow(block.id)}
                                >
                                  <AddIcon sx={{ fontSize: "22px" }} />
                                </Button>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {block.rows.map(row => (
                          <TableRow key={row.id}>
                            {/* Patient Type */}
                            <TableCell
                              sx={{
                                borderBottom: "none",
                                padding: "6px 4px",
                              }}
                            >
                              <TextField
                                select
                                size="small"
                                value={row.patientTypeId}
                                required
                                className="custom-input"
                                onChange={e =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "patientTypeId",
                                    e.target.value
                                  )
                                }
                                fullWidth
                                sx={{
                                  "& .MuiInputBase-input": {
                                    fontSize: "12px",
                                  },
                                }}
                                SelectProps={{
                                  displayEmpty: true,
                                  MenuProps: {
                                    PaperProps: {
                                      sx: {
                                        "& .MuiMenuItem-root": {
                                          fontSize: "14px",
                                          padding: "2px 8px",
                                          minHeight: "unset",
                                        },
                                      },
                                    },
                                  },
                                }}
                              >
                                <MenuItem value="">
                                  <em>Select type</em>
                                </MenuItem>
                                {patientTypes
                                  .filter(
                                    pt =>
                                      !block.rows.some(
                                        (r: any) =>
                                          r.patientTypeId === pt._id &&
                                          r.id !== row.id
                                      )
                                  )
                                  .map(pt => (
                                    <MenuItem key={pt._id} value={pt._id}>
                                      {pt.patientTypeName}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </TableCell>

                            {/* numeric fields unchanged */}
                            <TableCell
                              sx={{
                                borderBottom: "none",
                                padding: "6px 4px",
                              }}
                            >
                              <TextField
                                size="small"
                                type="number"
                                className="custom-input"
                                value={row.criticalMin}
                                onFocus={e => {
                                  if (e.target.value === "0")
                                    handleRowChange(
                                      block.id,
                                      row.id,
                                      "criticalMin",
                                      ""
                                    );
                                }}
                                onBlur={e => {
                                  if (e.target.value === "")
                                    handleRowChange(
                                      block.id,
                                      row.id,
                                      "criticalMin",
                                      0
                                    );
                                }}
                                fullWidth
                                onChange={e =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "criticalMin",
                                    e.target.value
                                  )
                                }
                                inputProps={{ step: "any" }}
                              />
                            </TableCell>

                            <TableCell sx={{ borderBottom: "none" }}>
                              <TextField
                                size="small"
                                type="number"
                                className="custom-input"
                                value={row.min}
                                onFocus={e => {
                                  if (e.target.value === "0")
                                    handleRowChange(
                                      block.id,
                                      row.id,
                                      "min",
                                      ""
                                    );
                                }}
                                fullWidth
                                onBlur={e => {
                                  if (e.target.value === "")
                                    handleRowChange(block.id, row.id, "min", 0);
                                }}
                                onChange={e =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "min",
                                    e.target.value
                                  )
                                }
                                inputProps={{ step: "any" }}
                              />
                            </TableCell>

                            <TableCell sx={{ borderBottom: "none" }}>
                              <TextField
                                size="small"
                                type="number"
                                className="custom-input"
                                value={row.max}
                                onFocus={e => {
                                  if (e.target.value === "0")
                                    handleRowChange(
                                      block.id,
                                      row.id,
                                      "max",
                                      ""
                                    );
                                }}
                                fullWidth
                                onBlur={e => {
                                  if (e.target.value === "")
                                    handleRowChange(block.id, row.id, "max", 0);
                                }}
                                onChange={e =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "max",
                                    e.target.value
                                  )
                                }
                                inputProps={{ step: "any" }}
                              />
                            </TableCell>

                            <TableCell sx={{ borderBottom: "none" }}>
                              <TextField
                                size="small"
                                className="custom-input"
                                type="number"
                                value={row.criticalMax}
                                onFocus={e => {
                                  if (e.target.value === "0")
                                    handleRowChange(
                                      block.id,
                                      row.id,
                                      "criticalMax",
                                      ""
                                    );
                                }}
                                onBlur={e => {
                                  if (e.target.value === "")
                                    handleRowChange(
                                      block.id,
                                      row.id,
                                      "criticalMax",
                                      0
                                    );
                                }}
                                fullWidth
                                onChange={e =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "criticalMax",
                                    e.target.value
                                  )
                                }
                                inputProps={{ step: "any" }}
                              />
                            </TableCell>

                            {/* NEW: Below min value text */}
                            {testType && (
                              <>
                                <TableCell
                                  sx={{ borderBottom: "none", width: 150 }}
                                >
                                  <TextField
                                    size="small"
                                    className="custom-input"
                                    placeholder="Low"
                                    value={row.belowMinText || "Low"}
                                    onChange={e =>
                                      handleRowChange(
                                        block.id,
                                        row.id,
                                        "belowMinText",
                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                    sx={{
                                      "& .MuiInputBase-input": {
                                        fontSize: "12px",
                                      },
                                    }}
                                  />
                                </TableCell>

                                <TableCell
                                  sx={{ borderBottom: "none", width: 120 }}
                                >
                                  <TextField
                                    size="small"
                                    className="custom-input"
                                    type="color"
                                    value={row.belowMinBg || "#091fb0"}
                                    onChange={e =>
                                      handleRowChange(
                                        block.id,
                                        row.id,
                                        "belowMinBg",

                                        e.target.value
                                      )
                                    }
                                    inputProps={{
                                      style: { height: 18, width: "200px" },
                                    }}
                                  />
                                </TableCell>

                                <TableCell
                                  sx={{ borderBottom: "none", width: 150 }}
                                >
                                  <TextField
                                    size="small"
                                    placeholder="Normal"
                                    className="custom-input"
                                    value={row.normalRangeText || "Normal"}
                                    onChange={e =>
                                      handleRowChange(
                                        block.id,
                                        row.id,
                                        "normalRangeText",

                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                  />
                                </TableCell>

                                <TableCell
                                  sx={{ borderBottom: "none", width: 120 }}
                                >
                                  <TextField
                                    size="small"
                                    type="color"
                                    className="custom-input"
                                    value={row.normalRangeBg || "#31F218"}
                                    onChange={e =>
                                      handleRowChange(
                                        block.id,
                                        row.id,
                                        "normalRangeBg",

                                        e.target.value
                                      )
                                    }
                                    inputProps={{
                                      style: { height: 18, width: "200px" },
                                    }}
                                  />
                                </TableCell>

                                <TableCell
                                  sx={{ borderBottom: "none", width: 150 }}
                                >
                                  <TextField
                                    size="small"
                                    placeholder="High"
                                    className="custom-input"
                                    value={row.aboveMaxText || "High"}
                                    onChange={e =>
                                      handleRowChange(
                                        block.id,
                                        row.id,
                                        "aboveMaxText",

                                        e.target.value
                                      )
                                    }
                                    fullWidth
                                  />
                                </TableCell>

                                {/* NEW: Above max value background color */}
                                <TableCell
                                  sx={{ borderBottom: "none", width: 120 }}
                                >
                                  <TextField
                                    size="small"
                                    type="color"
                                    className="custom-input"
                                    value={row.aboveMaxBg || "#a81103"}
                                    onChange={e =>
                                      handleRowChange(
                                        block.id,
                                        row.id,
                                        "aboveMaxBg",

                                        e.target.value
                                      )
                                    }
                                    inputProps={{
                                      style: { height: 18, width: "200px" },
                                    }}
                                  />
                                </TableCell>
                              </>
                            )}

                            {/* Min max value in words (existing) */}
                            <TableCell sx={{ borderBottom: "none" }}>
                              <TextField
                                size="small"
                                className="custom-input"
                                placeholder="value in words"
                                value={row.minmaxinwords || ""}
                                onChange={e =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "minmaxinwords",
                                    e.target.value
                                  )
                                }
                                fullWidth
                                sx={{
                                  "& .MuiInputBase-input": {
                                    fontSize: "12px",
                                  },
                                }}
                              />
                            </TableCell>

                            {/* Unit / CutOff (existing) */}
                            <TableCell sx={{ borderBottom: "none" }}>
                              <Autocomplete
                                freeSolo
                                size="small"
                                className="custom-input"
                                fullWidth
                                value={row.unit_cutOff || ""}
                                onChange={(_, newValue) =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "unit_cutOff",
                                    newValue || ""
                                  )
                                }
                                onInputChange={(_, newInputValue) =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "unit_cutOff",
                                    newInputValue || ""
                                  )
                                }
                                options={units.map(u => u.unitName)}
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    placeholder="Select Unit"
                                    size="small"
                                  />
                                )}
                                componentsProps={{
                                  popper: {
                                    sx: {
                                      "& .MuiAutocomplete-listbox .MuiAutocomplete-option":
                                        {
                                          fontSize: "14px",
                                          padding: "2px 8px",
                                          minHeight: "unset",
                                        },
                                    },
                                  },
                                }}
                              />
                            </TableCell>
                            
                            {/* Parameter Type */}
                            <TableCell sx={{ borderBottom: "none" }}>
                              <Autocomplete
                                size="small"
                                className="custom-input"
                                fullWidth
                                options={parameterTypes}
                                getOptionLabel={(option) => option.parameterType || ""}
                                isOptionEqualToValue={(option, value) =>
                                  option.parameterType === value.parameterType
                                }
                                value={
                                  parameterTypes.find(
                                    (pt) => pt.parameterType === row.parameterType
                                  ) || null
                                }
                                onChange={(_, newValue) =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "parameterType",
                                    newValue?.parameterType || ""
                                  )
                                }
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    placeholder="Select Parameter Type"
                                    size="small"
                                  />
                                )}
                                componentsProps={{
                                  popper: {
                                    sx: {
                                      "& .MuiAutocomplete-listbox .MuiAutocomplete-option":
                                        {
                                          fontSize: "14px",
                                          padding: "2px 8px",
                                          minHeight: "unset",
                                        },
                                    },
                                  },
                                }}
                              />
                            </TableCell>
                            
                            <TableCell
                              sx={{ borderBottom: "none", width: 200 }}
                            >
                              <Autocomplete
                                size="small"
                                className="custom-input"
                                fullWidth
                                options={resultTemplates}
                                getOptionLabel={option =>
                                  option.templateName || ""
                                }
                                isOptionEqualToValue={(option, value) =>
                                  option._id === value._id
                                }
                                value={
                                  resultTemplates.find(
                                    t => t._id === row.resultTemplateId
                                  ) || null
                                }
                                onChange={(_, newValue) =>
                                  handleRowChange(
                                    block.id,
                                    row.id,
                                    "resultTemplateId",
                                    newValue ? newValue._id : ""
                                  )
                                }
                                renderInput={params => (
                                  <TextField
                                    {...params}
                                    placeholder="Select Template Type"
                                    size="small"
                                  />
                                )}
                                componentsProps={{
                                  popper: {
                                    sx: {
                                      "& .MuiAutocomplete-listbox .MuiAutocomplete-option":
                                        {
                                          fontSize: "14px",
                                          padding: "2px 8px",
                                          minHeight: "unset",
                                        },
                                    },
                                  },
                                }}
                              />
                            </TableCell>

                            {/* action buttons */}
                            <TableCell
                              align="center"
                              sx={{ borderBottom: "none" }}
                            >
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() =>
                                  handleRemoveRow(block.id, row.id)
                                }
                              >
                                <Tooltip title="Close">
                                  <span>
                                    <CloseIcon fontSize="small" />
                                  </span>
                                </Tooltip>
                              </IconButton>

                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                  handleCopyToAll(block.id, row);
                                }}
                              >
                                <Tooltip title="Copy To ALL">
                                  <span>
                                    <ContentCopy sx={{ fontSize: "16px" }} />
                                  </span>
                                </Tooltip>
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>
              </Grid>
            </Paper>
          ))}
        </Box>
        <DeleteConfirmationModal
          open={isDeleteModalOpen}
          handleClose={() => setIsDeleteModalOpen(false)}
          handleConfirm={handleDeleteConfirm}
        />
      </Box>
    </>
  );
};

export default TestParameters;
